import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { CollectionLicensePricesModule } from '@app/collections/shared/collection-license-prices/collection-license-prices.module';
import { CollectionMetaModule } from '@app/collections/shared/collection-meta/collection-meta.module';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { loadMe } from '@app/store/actions/profile.actions';
import { AlertModule } from '@core/components/alert/alert.module';
import { BookmarkComponent } from '@core/components/bookmark/bookmark.component';
import { PipesModule } from '@core/pipes/pipes.module';
import { AccountService, BillingV2Service, CollectionsService, ContextService, FilesService } from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { LibraryRouter } from '@models/library.router';
import { Store } from '@ngrx/store';
import { RatingComponent } from '@shared/rating/rating.component';
import {
  Collection,
  CollectionLicenseEnum,
  IAccountStats,
  ImageSizeEnum,
  SubjectCategoryEnum,
  User,
  UserRoleEnum,
  UserSegmentEnum,
} from 'lingo2-models';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiButtonModule, OnUiCover, OnUiCoverModule, OnUiPopoverModule, PopoverPositionEnum } from 'onclass-ui';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-collection',
  standalone: true,
  imports: [
    LocaleSharedModule,
    BookmarkComponent,
    CommonModule,
    OnUiCoverModule,
    RatingComponent,

    CollectionMetaModule,
    PipesModule,
    OnUiPopoverModule,
    AlertModule,
    OnUiButtonModule,
    LazyLoadImageModule,
    CollectionLicensePricesModule,
  ],
  templateUrl: './collection.component.html',
  styleUrls: ['./collection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollectionComponent extends ChangableComponent implements OnInit {
  deviceService = inject(DeviceDetectorService);
  private collectionsService = inject(CollectionsService);
  private billingV2Service = inject(BillingV2Service);
  private contextService = inject(ContextService);
  private store = inject(Store);
  router = inject(Router);

  @Input() view: string;

  @Input() public set collection(collection: Collection) {
    this._collection = collection;
    this.prepareCard();
  }

  public get collection(): Collection {
    return this._collection;
  }

  @Output() public openInModal = new EventEmitter<Collection>();

  public collectionUrl: string;
  public editCollectionUrl: string;
  public subjectIsNative = false;
  public selectedLicense: CollectionLicenseEnum;
  public authorName: string;
  public authorAvatarUrl: string;
  public collectionDate: Date;
  public accountRoute: string[];
  public isValid: boolean;
  public fullTitle: string;
  public multiCoverUrls: string[] = [];
  public activeCoverItem = 0;
  public isDeleteAlertOpened = false;
  public complainFormOpened = false;
  public position: PopoverPositionEnum = PopoverPositionEnum.bottom;
  public betterLicense: CollectionLicenseEnum;
  public doHover = false;
  public userAvatar: OnUiCover;
  public authorStats: Partial<IAccountStats>;

  private _collection: Collection;
  public me: User;
  private collectionRoute = LibraryRouter.collectionRouteUniversal;

  public ngOnInit(): void {
    this.contextService.me$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (me) => {
        this.me = me;
        this.suggestLicense();
        this.detectChanges();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        this.store.dispatch(loadMe());
        this.suggestLicense();
      },
    });
  }

  public getFormattedPrice(amount: number, discount?: number): Observable<string> {
    return this.billingV2Service.formatAmount$(amount, discount);
  }

  private prepareCard() {
    this.fullTitle = this.collectionsService.collectionFullTitle(this._collection);
    this.collectionUrl = this.collectionRoute(this._collection);
    this.editCollectionUrl = '/content/collections/edit/' + this._collection.id;
    this.subjectIsNative = this._collection?.subject?.category_id === SubjectCategoryEnum.native_language;
    this.collectionDate = this._collection
      ? this._collection.changed_at
        ? new Date(this._collection.changed_at)
        : new Date(this._collection.created_at)
      : new Date();
    this.isValid = this._collection?.valid;

    this.authorName = AccountService.getUserCropName(this._collection?.author);
    this.authorAvatarUrl = this._collection?.author?.__opts?.image?.img__smX2
      ? this.prepareStorageUrl(this._collection?.author?.__opts?.image?.img__smX2)
      : FilesService.getFileUrlBySize(this._collection?.author?.userpic_id, ImageSizeEnum.sm);
    this.authorStats = this._collection?.author?.stats;
    this.userAvatar = {
      type: 'avatar',
      img: this.authorAvatarUrl,
      title: this.authorName,
      form: 'circle',
    };

    this.prepareCover();
    this.detectChanges();
  }

  public get isAuthor(): boolean {
    const me_id = this.me?.id;
    const author_id = this._collection?.author?.id || this._collection?.author_id;
    return me_id === author_id;
  }

  public get isModerator(): boolean {
    return this.me && AccountService.hasRole(this.me, UserRoleEnum.moderator);
  }

  public onToggleDeleteAlert() {
    this.isDeleteAlertOpened = !this.isDeleteAlertOpened;
    this.detectChanges();
  }

  public get purchasedLicense(): CollectionLicenseEnum {
    return this._collection.visit_info?.licensed;
  }

  protected suggestLicense() {
    const price_tiers = this.collection.price_tiers || {};
    let licenseOrder: CollectionLicenseEnum[];
    const isTeacher = AccountService.isAsIfTeacher(this.me);
    const isSchoolOwner = AccountService.hasSegment(this.me, UserSegmentEnum.school_owner);
    if (isSchoolOwner) {
      licenseOrder = [
        CollectionLicenseEnum.business,
        CollectionLicenseEnum.professional,
        CollectionLicenseEnum.personal,
      ];
    } else if (isTeacher) {
      licenseOrder = [
        CollectionLicenseEnum.professional,
        CollectionLicenseEnum.personal,
        CollectionLicenseEnum.business,
      ];
    } else {
      licenseOrder = [
        CollectionLicenseEnum.personal,
        CollectionLicenseEnum.professional,
        CollectionLicenseEnum.business,
      ];
    }
    this.betterLicense = licenseOrder.find((c) => c in price_tiers && price_tiers[c] > 0);
    if (!this.selectedLicense && !!this.purchasedLicense) {
      this.selectedLicense = this.purchasedLicense;
    }

    if (!this.selectedLicense && this.me && this.collection) {
      this.selectedLicense = this.betterLicense;
    }
  }

  public get selectedLicensePrice(): number {
    const price_tiers = this.collection.price_tiers || {};
    return this.selectedLicense ? price_tiers[this.selectedLicense] : null;
  }

  private prepareCover() {
    this.multiCoverUrls = [];
    if (this._collection?.__opts?.images) {
      this._collection?.__opts?.images
        .map((imageArray) => {
          this.multiCoverUrls.push(this.prepareStorageUrl(imageArray.md));
        })
        .slice(0, 4);
      this.detectChanges();
    } else if (this._collection?.multi_cover_id) {
      this._collection?.multi_cover_id
        .map((imageId) => {
          this.multiCoverUrls.push(FilesService.getFileUrlBySize(imageId, ImageSizeEnum.md));
        })
        .slice(0, 4);
      this.detectChanges();
    }
  }

  public getAvatar(url?: string): OnUiCover {
    return {
      img: url ? url : null,
      title: this.fullTitle,
    };
  }

  public setActiveCoverItem(index: number) {
    this.activeCoverItem = index;
    this.detectChanges();
  }

  public onOpenCardInModal() {
    this.openInModal.emit(this._collection);
  }

  public favoriteChanged(event) {
    this._collection.visit_info.is_favorite = event;
  }

  public trackByFn(index) {
    return index;
  }
}
